import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type {
  AspectRatio,
  BrandCalendarSettings,
  CalendarMediaType,
  CalendarMonthPlan,
  CalendarMonthPlanListRow,
  CalendarMonthPlanWithSlots,
  MonthCadenceInput,
  CalendarOrientation,
  CalendarPlanSlot,
  CalendarPostRisk,
  CalendarSettingsInput,
  CalendarSlotStatus,
  CalendarVideoLength,
  PlanBriefInput,
  PlanSlotInput,
  SendForReviewResult,
  ClientNotifyState,
  NotifyClientsResult,
} from "@/types/contentCalendar.type";
import { clampWhenHidden } from "@/lib/realtime";
import { monthLabel } from "../calendar.utils";
import {
  cancelPlanFetches,
  moveSlotInCache,
  patchSlot,
  patchSlotsWhere,
  planKey,
  planQueryOptions,
  planWriteKey,
  putPlan,
  putSlot,
  readPlan,
  readSlot,
  reconcilePlan,
  removeSlotFromCache,
  restoreSlotToCache,
  settlePlanWrite,
} from "./calendarCache";
import { planPollMs, plansListPollMs } from "./planPolling";
import { livePoll, useCalendarLive } from "./calendarLive";
import { distributionKey } from "./useScheduling";

const settingsKey = (brandId: string | null) => ["calendarSettings", brandId];
const plansKey = (brandId: string | null) => ["calendarPlans", brandId];
const notifyStateKey = (planId: string | null) => ["calendarNotifyState", planId];

function errorMessage(err: unknown, fallback: string): string {
  return getAiRequestErrorMessage(err, fallback);
}

// ---------------------------------------------------------------- Settings

/** The brand's planning cadence. The backend creates defaults on first read. */
export function useCalendarSettings(brandId: string | null) {
  return useQuery({
    queryKey: settingsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ settings: BrandCalendarSettings }>(
        `/content-calendar/${brandId}/settings`,
      );
      return res.data.settings;
    },
    enabled: !!brandId,
  });
}

/**
 * Change the cadence. Deliberately does not touch months already planned —
 * the new numbers apply to the next month the worker lays out.
 */
export function useUpdateCalendarSettings(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CalendarSettingsInput) => {
      const res = await axiosInstance.patch<{ settings: BrandCalendarSettings }>(
        `/content-calendar/${brandId}/settings`,
        payload,
      );
      return res.data.settings;
    },
    onSuccess: (settings) => {
      qc.setQueryData(settingsKey(brandId), settings);
      queue.add({
        title: "Cadence saved",
        description: "Applies to the next month the planner lays out.",
        variant: "success",
      });
    },
    onError: (err) => {
      queue.add({
        title: "Could not save the cadence",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

/**
 * ONE month's cadence, as distinct from the brand's. Pins the month to the
 * numbers given (creating its plan row if it has none yet), or hands it back to
 * the brand's cadence with `useBrandCadence`. The month's targets change on the
 * spot, so both the list row and the open plan are refreshed.
 */
export function useUpdateMonthCadence(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      year,
      month,
      ...input
    }: { year: number; month: number } & MonthCadenceInput) => {
      const res = await axiosInstance.patch<{ plan: CalendarMonthPlan }>(
        `/content-calendar/${brandId}/months/${year}/${month}/cadence`,
        input,
      );
      return res.data.plan;
    },
    onSuccess: (plan, vars) => {
      void qc.invalidateQueries({ queryKey: plansKey(brandId) });
      reconcilePlan(qc, plan.id);
      queue.add({
        title: "useBrandCadence" in vars ? "Back on the brand's cadence" : "Cadence saved",
        description: `${monthLabel({ year: vars.year, month: vars.month })} only. The brand's cadence is unchanged.`,
        variant: "success",
      });
    },
    onError: (err) => {
      queue.add({
        title: "Could not save this month's cadence",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

// ---------------------------------------------------------------- Pillars

export interface CalendarPillar {
  id: string;
  name: string;
  definition: string;
  /** Why the pillar matters: the audience insight it answers (the idea slide's Insight). */
  whyItMatters?: string;
  distributionPct: number;
  order: number;
}

/**
 * The pillars a post can serve — the brand's ACTIVE strategy's promoted set.
 * An empty list with a null strategyId is the same condition that blocks plan
 * generation, so the UI can say "approve a strategy first" from this one call.
 */
export function useCalendarPillars(brandId: string | null) {
  return useQuery({
    queryKey: ["calendarPillars", brandId],
    queryFn: async () => {
      // `objective`: the strategy's lead SMART objective, for the idea slide's Objective.
      const res = await axiosInstance.get<{ strategyId: string | null; pillars: CalendarPillar[]; objective?: string | null }>(
        `/content-calendar/${brandId}/pillars`,
      );
      return res.data;
    },
    enabled: !!brandId,
  });
}

// ---------------------------------------------------------------- Plans

export function useCalendarPlans(brandId: string | null) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: plansKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ plans: CalendarMonthPlanListRow[] }>(
        `/content-calendar/${brandId}/plans`,
      );
      return res.data.plans;
    },
    enabled: !!brandId,
    // Statuses in this list change without anyone here doing anything: planning
    // and brief fan-outs finish, clients close review rounds, the 01:00
    // auto-planner creates months. Those arrive as realtime hints; without the
    // socket, plansListPollMs never returns false — the list idles rather than
    // stalls.
    refetchInterval: (query) => livePoll(live, clampWhenHidden(plansListPollMs(query.state.data))),
    refetchIntervalInBackground: !live,
    refetchOnWindowFocus: !live,
  });
}

export function useCalendarPlan(planId: string | null) {
  const qc = useQueryClient();
  const live = useCalendarLive();
  return useQuery({
    ...planQueryOptions(qc, planId),
    // Everything that writes this month — plan/idea/brief fan-outs, rework
    // after a decline, the ideation and recovery crons, client decisions — is
    // fire-and-forget from this tab's point of view, and arrives as a realtime
    // hint. Without the socket, planPollMs picks the cadence and only stops for
    // signed-off/archived months.
    refetchInterval: (query) => livePoll(live, clampWhenHidden(planPollMs(query.state.data))),
    refetchIntervalInBackground: !live,
    refetchOnWindowFocus: !live,
  });
}

/**
 * Plan a month now. Fire-and-forget: the plan flips to GENERATING and the
 * caller polls. Refuses months already past, and approved months (their ideas
 * would be destroyed).
 *
 * `force` is the recovery path for a month frozen on "Planning": it lets a
 * stalled GENERATING claim be taken past the force floor rather than after the
 * full 45-minute stale window. The floor is enforced server-side; this flag only
 * asks.
 *
 * `guidance` and `referenceDocument` are the operator's brief for THIS run. They
 * travel with the request, steer the run, and are never stored — there is
 * nothing to fetch back, which is why they are not in any query key.
 */
export function useGeneratePlan(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: PlanBriefInput & { year?: number; month?: number; force?: boolean; quiet?: boolean },
    ) => {
      const { quiet: _quiet, ...body } = payload;
      void _quiet;
      const res = await axiosInstance.post<{ plan: CalendarMonthPlanWithSlots }>(
        `/content-calendar/${brandId}/plans/generate`,
        body,
      );
      return res.data.plan;
    },
    onSuccess: (plan, vars) => {
      putPlan(qc, plan.id, plan);
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      // The Plan button's deterministic start says it on the month itself (and in the chat).
      if (vars.quiet) return;
      queue.add({
        title: vars.force ? "Restarting the plan" : "Planning the month",
        description: vars.force
          ? "The stalled run was dropped — the agents are laying the month out again."
          : "The agents are laying out the posts — this takes a few minutes.",
        variant: "info",
      });
    },
    onError: (err) => {
      queue.add({
        title: "Could not plan the month",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

/**
 * The team's note while a month plans (Figma 08, 13 Oct: "While I plan January:
 * what should it do for the brand? Anything to highlight?"). Saved on the
 * GENERATING plan; the runner reads it for the layout when it is still in time
 * and for every post's ideas. A 409 `not_planning` means the month has landed:
 * the caller sends the words to Creo in the chat instead.
 */
export function useSetPlanningGuidance() {
  return useMutation({
    mutationFn: async ({ planId, text }: { planId: string; text: string }) => {
      const res = await axiosInstance.patch<{ planId: string; saved: true }>(
        `/content-calendar/plans/${planId}/guidance`,
        { text },
      );
      return res.data;
    },
  });
}

/** The 409 a planning note or a Stop gets once the month is no longer planning. */
export function isNotPlanning(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 409 && (err.response.data as { code?: string } | undefined)?.code === "not_planning";
}

/**
 * Stop planning (Figma 08, 13 Oct): the run's result is thrown away and the
 * month is not planned. Optimistic: the month reads not planned at once; a
 * refusal (the month already landed) puts the plan row back and says so.
 */
export function useCancelPlanning(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "cancelPlanning"],
    mutationFn: async () => {
      const res = await axiosInstance.post<{ plan: CalendarMonthPlanWithSlots }>(
        `/content-calendar/plans/${planId}/cancel-planning`,
      );
      return res.data.plan;
    },
    onMutate: async () => {
      await cancelPlanFetches(qc, planId);
      const previous = planId ? qc.getQueryData<CalendarMonthPlanWithSlots>(planKey(planId)) : undefined;
      if (previous) putPlan(qc, planId!, { ...previous, status: "DRAFT", slots: [] });
      return { previous };
    },
    onSuccess: (plan) => {
      putPlan(qc, plan.id, plan);
      queue.add({ title: "Planning stopped.", description: "Nothing was placed. Plan it again whenever you like.", variant: "success" });
    },
    onError: (err, _vars, context) => {
      if (context?.previous && planId) putPlan(qc, planId, context.previous);
      queue.add({
        title: isNotPlanning(err) ? "Too late to stop: the month is already planned" : "Could not stop planning",
        description: isNotPlanning(err) ? "Re-plan it from the month menu if it is not what you wanted." : errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: () => {
      settlePlanWrite(qc, planId, { refetch: true });
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
    },
  });
}

/**
 * Wipe a month's posts and plan it again, at any live stage — the backend
 * refuses only an archived month. Destructive: always confirm before calling.
 *
 * Optimistic, and here the optimism is simply the truth: the server empties the
 * month the moment it accepts the request, so the month is shown empty and
 * planning straight away. A refusal puts the posts back.
 *
 * Takes the same one-off brief as `useGeneratePlan`, and it earns its place
 * here: the caller has just read a month and rejected it, so this is where they
 * have something specific to say. Stored nowhere, same as the other path.
 */
export function useRegeneratePlan(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "regenerate"],
    mutationFn: async (brief: PlanBriefInput) => {
      const res = await axiosInstance.post<{ plan: CalendarMonthPlanWithSlots }>(
        `/content-calendar/plans/${planId}/regenerate`,
        brief,
      );
      return res.data.plan;
    },
    onMutate: async () => {
      await cancelPlanFetches(qc, planId);
      const previous = readPlan(qc, planId);
      if (previous) putPlan(qc, planId, { ...previous, status: "GENERATING", slots: [] });
      return { previous };
    },
    onSuccess: () => {
      queue.add({
        title: "Re-planning the month",
        description:
          "The previous posts were cleared — the agents are laying out new ones. Concepts start when you ask for them.",
        variant: "info",
      });
    },
    onError: (err, _brief, ctx) => {
      // The whole month is the right undo here: the click emptied all of it.
      if (ctx?.previous) putPlan(qc, planId, ctx.previous);
      queue.add({
        title: "Could not re-plan the month",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: (plan, err) => {
      void qc.invalidateQueries({ queryKey: plansKey(brandId) });
      settlePlanWrite(qc, planId, err ? { refetch: true } : { plan });
    },
  });
}

/** A send's posts, with the day the client should answer by (YYYY-MM-DD) and a note (#130). */
export interface SendForReviewInput {
  slotIds?: string[];
  answerBy?: string | null;
  note?: string | null;
}

/**
 * Explicitly share chosen concepts as immutable client versions. Notification
 * is a separate transport step coordinated by the single send dialog.
 */
export function useSendPlanForReview(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "sendForReview"],
    mutationFn: async (input?: string[] | SendForReviewInput) => {
      // A bare id list is the old call; the object adds the answer-by day and the note (#130).
      const { slotIds, answerBy, note } = Array.isArray(input) || !input ? { slotIds: input } : input;
      const res = await axiosInstance.post<SendForReviewResult>(
        `/content-calendar/plans/${planId}/send-for-review`,
        {
          ...(slotIds && slotIds.length > 0 ? { slotIds } : {}),
          ...(answerBy ? { answerBy } : {}),
          ...(note?.trim() ? { note: note.trim() } : {}),
        },
      );
      return res.data;
    },
    /*
     * The share is applied on the click, like every other calendar write. The
     * server's rule is exactly reproducible here — a batch takes the pipeline
     * posts sitting at SIGNED_OFF, and refuses outright if any named post is
     * not one — so the optimistic month is the same month the response carries,
     * and a rejected send is an all-or-nothing rollback of the posts it moved
     * (never the whole month: another write may be in flight on the others).
     */
    onMutate: async (input): Promise<{ sent: CalendarPlanSlot[] }> => {
      const slotIds = Array.isArray(input) || !input ? input : input.slotIds;
      await cancelPlanFetches(qc, planId);
      const named = slotIds && slotIds.length > 0 ? new Set(slotIds) : null;
      // `listSendableSlots`, restated: a pipeline post, signed off, carrying
      // its accepted idea. Omitting the ids means "everything ready".
      const sendable = (slot: CalendarPlanSlot) =>
        slot.status === "SIGNED_OFF" &&
        slot.origin !== "DIRECT" &&
        (slot.ideas ?? []).some((idea) => idea.status === "ACCEPTED") &&
        (named ? named.has(slot.id) : true);
      const sent = (readPlan(qc, planId)?.slots ?? []).filter(sendable);
      patchSlotsWhere(qc, planId, sendable, (slot) => ({ ...slot, status: "IN_CLIENT_REVIEW" }));
      return { sent };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      qc.invalidateQueries({ queryKey: ["conceptVersions"] });
      const n = result.round.awaiting;
      queue.add({
        title: `${n} content idea${n === 1 ? "" : "s"} shared`,
        description: "The selected concept version is now on the client page. Email delivery is shown separately in the send dialog.",
        variant: "success",
      });
    },
    onError: (err, _slotIds, undo) => {
      for (const slot of undo?.sent ?? []) putSlot(qc, planId, slot);
      queue.add({
        title: "Could not share those posts",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    // A refusal is usually a 409 (something moved under us), so the restored
    // posts are refetched too.
    onSettled: (result, err) =>
      settlePlanWrite(qc, planId, err ? { refetch: true } : { plan: result?.plan }),
  });
}

/** The combined send (Figma 08): posts, the reply-by day, a note, and who gets the email. */
export interface SendPostsInput {
  slotIds: string[];
  answerBy?: string | null;
  note?: string | null;
  /** Email them now. Every guest sees the send either way. */
  notify?: boolean;
  /** Who gets the email (the dialog's "Who gets it"). */
  notifyUserIds?: string[];
}

/**
 * Send each post's concept AND its creative brief to the client in one go
 * (POST send-posts): one answer per post, one email to the guests picked.
 * Optimistic like the old send: the posts move to "with the client" on the
 * click and only the posts it moved roll back on a refusal.
 */
export function useSendPosts(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "sendPosts"],
    mutationFn: async (input: SendPostsInput) => {
      const res = await axiosInstance.post<SendForReviewResult>(`/content-calendar/plans/${planId}/send-posts`, {
        slotIds: input.slotIds,
        ...(input.answerBy ? { answerBy: input.answerBy } : {}),
        ...(input.note?.trim() ? { note: input.note.trim() } : {}),
        ...(input.notify ? { notify: true, notifyUserIds: input.notifyUserIds } : {}),
      });
      return res.data;
    },
    onMutate: async (input): Promise<{ sent: CalendarPlanSlot[] }> => {
      await cancelPlanFetches(qc, planId);
      const named = new Set(input.slotIds);
      const sendable = (slot: CalendarPlanSlot) => slot.status === "SIGNED_OFF" && slot.origin !== "DIRECT" && named.has(slot.id);
      const sent = (readPlan(qc, planId)?.slots ?? []).filter(sendable);
      const sentAt = new Date().toISOString();
      patchSlotsWhere(qc, planId, sendable, (slot) => ({
        ...slot,
        status: "IN_CLIENT_REVIEW",
        clientReview: {
          brief: null,
          ...slot.clientReview,
          concept: {
            roundId: "pending",
            sentAt,
            answerBy: input.answerBy ? `${input.answerBy}T00:00:00.000Z` : null,
            note: input.note?.trim() || null,
            late: false,
            lastReminder: null,
            openedAt: null,
            openedByName: null,
          },
        },
      }));
      return { sent };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      qc.invalidateQueries({ queryKey: ["conceptVersions"] });
      qc.invalidateQueries({ queryKey: ["planBriefs", planId] });
      qc.invalidateQueries({ queryKey: notifyStateKey(planId) });
    },
    onError: (err, _input, undo) => {
      for (const slot of undo?.sent ?? []) putSlot(qc, planId, slot);
      queue.add({ title: "Nothing was sent", description: errorMessage(err, "Please try again."), variant: "error" });
    },
    onSettled: (result, err) => settlePlanWrite(qc, planId, err ? { refetch: true } : { plan: result?.plan }),
  });
}

/** Pull every open idea batch back — the walked-away-client escape hatch. */
export function useRecallClientReview(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "recallReview"],
    mutationFn: async () => {
      const res = await axiosInstance.post<{ plan: CalendarMonthPlanWithSlots }>(
        `/content-calendar/plans/${planId}/recall-review`,
      );
      return res.data.plan;
    },
    onSettled: (plan, err) => settlePlanWrite(qc, planId, err ? { refetch: true } : { plan }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      queue.add({
        title: "Review pulled back",
        description:
          "The undecided posts are yours again. Answers the client already gave still stand.",
      });
    },
    onError: (err) => {
      queue.add({
        title: "Could not pull the review back",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

/**
 * Take ONE post back off the client's board. The everyday undo for an accept —
 * and the only way to move a post's day again once the client can see it.
 */
/**
 * Pull a post back from the client: its idea and brief leave their page
 * together (Figma 08 "Pulled back from Fatafeat." with Undo). `onUndo` sends
 * it again, when the page can.
 */
export function useRecallSlotFromClient(
  brandId: string | null,
  planId: string | null,
  options?: { clientName?: string | null; onUndo?: (slotId: string) => void },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "recallSlot"],
    mutationFn: async (slotId: string) => {
      const res = await axiosInstance.post<{ plan: CalendarMonthPlanWithSlots }>(
        `/content-calendar/plans/${planId}/slots/${slotId}/recall`,
      );
      return res.data.plan;
    },
    // The twin of the share above, and optimistic for the same reason: this is
    // the undo for a one-click act, and an undo that spins is not an undo.
    onMutate: async (slotId): Promise<{ slot?: CalendarPlanSlot }> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      patchSlotsWhere(
        qc,
        planId,
        (s) => s.id === slotId && s.status === "IN_CLIENT_REVIEW",
        (s) => ({ ...s, status: "SIGNED_OFF" }),
      );
      return { slot };
    },
    onSettled: (plan, err) => settlePlanWrite(qc, planId, err ? { refetch: true } : { plan }),
    onSuccess: (_plan, slotId) => {
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      qc.invalidateQueries({ queryKey: notifyStateKey(planId) });
      qc.invalidateQueries({ queryKey: ["planBriefs", planId] });
      const client = options?.clientName?.trim();
      queue.add({
        title: client ? `Pulled back from ${client}.` : "Pulled back.",
        description: "It is off their page and yours again.",
        variant: "success",
        ...(options?.onUndo ? { action: { label: "Undo", onPress: () => options.onUndo!(slotId) }, countdown: 8 } : {}),
      });
    },
    // The likely 409 is "the client just answered it": the settle refetches, no guessing.
    onError: (err, _slotId, undo) => {
      if (undo?.slot) putSlot(qc, planId, undo.slot);
      queue.add({
        title: "Could not pull that post back",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

/**
 * What's waiting for the client and when each of the brand's guests was last
 * emailed — read before the operator decides whether this is worth an
 * interruption.
 *
 * Not polled: it is dialog-time information, and it changes only when the
 * operator themselves accepts, pulls back, or notifies.
 */
export function useClientNotifyState(planId: string | null, enabled = true) {
  return useQuery({
    queryKey: notifyStateKey(planId),
    queryFn: async () => {
      const res = await axiosInstance.get<ClientNotifyState>(
        `/content-calendar/plans/${planId}/notify-state`,
      );
      return res.data;
    },
    enabled: !!planId && enabled,
  });
}

/**
 * THE email. One message per guest about the whole month, sent because a
 * person chose to send it. It links to the month in their account.
 */
export function useNotifyClients(planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userIds?: string[]) => {
      const res = await axiosInstance.post<NotifyClientsResult>(
        `/content-calendar/plans/${planId}/notify`,
        userIds && userIds.length > 0 ? { userIds } : {},
      );
      return res.data;
    },
    onSuccess: (result) => {
      qc.setQueryData(notifyStateKey(planId), result.state);
      queue.add({
        title:
          result.failed > 0
            ? `Emailed ${result.notified}, ${result.failed} could not be reached`
            : `Emailed ${result.notified} client${result.notified === 1 ? "" : "s"}`,
        description:
          result.failed > 0
            ? `Check the address for ${result.failures.map((f) => f.email).join(", ")}.`
            : "The email takes them to this month in their account.",
        variant: result.failed > 0 ? "error" : "success",
      });
    },
    onError: (err) => {
      queue.add({
        title: "Could not send that email",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

/**
 * Create a DIRECT ("quick") post — ready-made content dropped on a day. Returns
 * the new slot plus the plan it landed in, since the month may not have had a
 * plan row at all before this.
 */
export function useCreateDirectSlot(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      date: string;
      mediaType: CalendarMediaType;
      aspectRatio: AspectRatio;
      orientation: CalendarOrientation;
      videoLength?: CalendarVideoLength | null;
      channels?: string[];
      /** Static only: 2 or more makes it a carousel with that many slides. */
      slideCount?: number | null;
    }) => {
      const res = await axiosInstance.post<{ slot: CalendarPlanSlot; planId: string }>(
        `/content-calendar/${brandId}/direct-slots`,
        input,
      );
      return res.data;
    },
    onSuccess: (result) => {
      reconcilePlan(qc, result.planId);
      void qc.invalidateQueries({ queryKey: plansKey(brandId) });
      queue.add({
        title: "Quick post added",
        description: "Upload the media and pick a time — it skips ideas and briefs entirely.",
      });
    },
    onError: (err) => {
      queue.add({
        title: "Could not add the post",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

// ---------------------------------------------------------------- Plan slots

export function useAddPlanSlot(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "add"],
    mutationFn: async (slot: PlanSlotInput) => {
      const res = await axiosInstance.post<{ slot: CalendarPlanSlot }>(
        `/content-calendar/plans/${planId}/slots`,
        slot,
      );
      return res.data.slot;
    },
    onSettled: () => settlePlanWrite(qc, planId, { refetch: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      queue.add({ title: "Post added", variant: "success" });
    },
    onError: (err) => {
      queue.add({
        title: "Could not add the post",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

export function useUpdatePlanSlot(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "update"],
    mutationFn: async (payload: { slotId: string; slot: Partial<PlanSlotInput> }) => {
      const res = await axiosInstance.patch<{ slot: CalendarPlanSlot }>(
        `/content-calendar/plan-slots/${payload.slotId}`,
        payload.slot,
      );
      return res.data.slot;
    },
    // The reply is the bare row, not the month's hydrated post: refetch instead of laying it over.
    onSettled: () => settlePlanWrite(qc, planId, { refetch: true }),
    onSuccess: (_slot, payload) => {
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
      // A channels change re-fans-out the publications server-side — the
      // distribution panel must not keep showing the old platform set.
      qc.invalidateQueries({ queryKey: distributionKey(payload.slotId) });
      queue.add({ title: "Post saved", variant: "success" });
    },
    onError: (err) => {
      queue.add({
        title: "Could not save the post",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

export interface MovePlanSlotInput {
  slotId: string;
  /** The UTC day the card was dropped on, as an ISO day. */
  scheduledAt: string;
  /** Insert index within that day; omitted means last. */
  position?: number;
  /** The status the move implies — computed by the caller, confirmed by the server. */
  status: CalendarSlotStatus;
}

/**
 * Drag-and-drop reschedule. Its own endpoint, not a `PATCH` with a date: a move
 * is legal on a signed-off post and the other slot edits are not.
 *
 * Optimistic, and deliberately so — a dragged card that snapped back to its old
 * day for a round-trip before landing would read as a failed drag every time.
 * A move the server refuses (the month went to the client while the card was in
 * the air) puts that card back and says which; a second drag still in the air
 * keeps its own move.
 */
export function useMovePlanSlot(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "move"],
    mutationFn: async ({ slotId, scheduledAt, position }: MovePlanSlotInput) => {
      const res = await axiosInstance.patch<{ slot: CalendarPlanSlot }>(
        `/content-calendar/plan-slots/${slotId}/move`,
        { scheduledAt, position },
      );
      return res.data.slot;
    },
    onMutate: async (move) => {
      await cancelPlanFetches(qc, planId);
      const previous = readSlot(qc, planId, move.slotId);
      moveSlotInCache(qc, planId, move);
      return { previous };
    },
    onError: (err, _move, context) => {
      if (context?.previous) putSlot(qc, planId, context.previous);
      queue.add({
        title: "Could not move the post",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    // No success toast: the card moving IS the confirmation. The refetch settles
    // the orders of both days (the source day's run is left gappy by the move).
    onSettled: () => {
      settlePlanWrite(qc, planId, { refetch: true });
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
    },
  });
}

/** How long a deleted post can still be brought back (the toast's Undo): eight seconds, like every calendar Undo (Figma 08). */
const DELETE_UNDO_MS = 8_000;
/** Posts whose delete is held for Undo, and how to call it off. */
const heldDeletes = new Map<string, () => void>();
class DeleteUndone extends Error {}

/**
 * Delete a post (Figma 08 "Post deleted from Tue 15 Dec." with Undo). The post
 * leaves every surface on confirm; the delete itself waits out the toast, so
 * Undo calls it off and the post comes back as it was. The server deletes for
 * good, so there is nothing to restore once it has run.
 */
export function useDeletePlanSlot(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "delete"],
    mutationFn: async (slotId: string) => {
      await new Promise<void>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          heldDeletes.delete(slotId);
          resolve();
        }, DELETE_UNDO_MS);
        heldDeletes.set(slotId, () => {
          window.clearTimeout(timer);
          heldDeletes.delete(slotId);
          reject(new DeleteUndone());
        });
      });
      await axiosInstance.delete(`/content-calendar/plan-slots/${slotId}`);
      return slotId;
    },
    // Optimistic: the post leaves every calendar surface on confirm, and only
    // this post comes back if the server refuses or Undo is pressed.
    onMutate: async (slotId) => {
      await cancelPlanFetches(qc, planId);
      const removed = removeSlotFromCache(qc, planId, slotId);
      const day = removed?.slot?.scheduledAt
        ? new Date(removed.slot.scheduledAt).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).replace(",", "")
        : null;
      queue.add({
        title: day ? `Post deleted from ${day}.` : "Post deleted.",
        variant: "success",
        action: { label: "Undo", onPress: () => heldDeletes.get(slotId)?.() },
        countdown: DELETE_UNDO_MS / 1000,
      });
      return { removed };
    },
    onError: (err, _slotId, context) => {
      if (context?.removed) restoreSlotToCache(qc, planId, context.removed);
      if (err instanceof DeleteUndone) return;
      queue.add({
        title: "Could not remove the post",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    // Reconcile once the last write of a batch settles: a refetch between them
    // would briefly bring back posts whose requests are still in flight.
    onSettled: () => {
      settlePlanWrite(qc, planId, { refetch: true });
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
    },
  });
}

/**
 * Act on a post's at-risk suggestion (backend #126): accept (a move moves the
 * post, every other fix is only recorded) or dismiss. Optimistic: the warning
 * leaves the post, and an accepted move lands on its day, at once.
 */
export function useDecidePostRisk(brandId: string | null, planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "decideRisk"],
    mutationFn: async ({ risk, decision }: { risk: CalendarPostRisk; decision: "accept" | "dismiss" }) => {
      const res = await axiosInstance.post<{ slot: CalendarPlanSlot | null }>(
        `/content-calendar/risks/${risk.id}/${decision}`,
      );
      return res.data.slot;
    },
    onMutate: async ({ risk, decision }) => {
      await cancelPlanFetches(qc, planId);
      const previous = readSlot(qc, planId, risk.slotId);
      patchSlot(qc, planId, risk.slotId, (slot) => ({ ...slot, atRisk: null }));
      if (decision === "accept" && risk.fix.kind === "move" && risk.fix.day && previous) {
        moveSlotInCache(qc, planId, { slotId: risk.slotId, scheduledAt: risk.fix.day, status: previous.status });
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) putSlot(qc, planId, context.previous);
      queue.add({
        title: "Could not act on the suggestion",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: () => {
      settlePlanWrite(qc, planId, { refetch: true });
      qc.invalidateQueries({ queryKey: plansKey(brandId) });
    },
  });
}
